import React from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import type { TableSchema } from '@/domains/diagram/shapes/table/tableSchema.types';
import { getAllTableSchemas, isCustomSchema } from '@/domains/diagram/shapes/table/tableSchemas.registry';
import { createTableFromSchema } from '@/domains/diagram/shapes/table/createTableFromSchema';
import { useDiagramStore } from '@/stores/diagramStore';
import { getCommandManager } from '@/hooks/useCommandManager';
import { cn } from '@/lib/utils';
import {
  filterShapePickerItems,
  normalizeShapePickerSearch,
} from '@/lib/diagram/shapePickerSearch';
import { Trash2, MoreVertical, Upload, Download } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { deleteCustomSchema, importSchemaFromJSON, saveCustomSchema, exportSchemaAsJSON, getCustomSchema } from '@/domains/diagram/shapes/table/customTableSchemas';
import { SaveTableTemplateDialog } from './SaveTableTemplateDialog';
import { toast } from 'sonner';
import { useConfirmationDialog } from '@/components/ui/use-confirmation-dialog';

interface TableTemplatePickerProps {
  onTemplateCreated?: () => void;
  searchQuery?: string;
}

export const TableTemplatePicker: React.FC<TableTemplatePickerProps> = ({ onTemplateCreated, searchQuery = '' }) => {
  const { t } = useTranslation('common');
  const { requestConfirmation, confirmationDialog } = useConfirmationDialog();
  const stage = useDiagramStore((s) => s.stage);
  const canWrite = useDiagramStore((s) => s.canWrite);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [importDialogOpen, setImportDialogOpen] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const schemas: TableSchema[] = React.useMemo(() => {
    void refreshKey;
    return getAllTableSchemas().filter((schema) => schema.id !== 'blank');
  }, [refreshKey]);

  const filteredSchemas = React.useMemo(
    () =>
      filterShapePickerItems(schemas, searchQuery, (schema) => [
        schema.id,
        schema.name,
        schema.category,
      ]),
    [schemas, searchQuery],
  );
  const hasSearchQuery = normalizeShapePickerSearch(searchQuery).length > 0;

  const handleDeleteSchema = (schemaId: string) => {
    if (isCustomSchema(schemaId)) {
      deleteCustomSchema(schemaId);
      setRefreshKey((k) => k + 1);
      const evt = new CustomEvent('table-template-deleted', { detail: { schemaId } });
      window.dispatchEvent(evt);
    }
  };

  const handleImport = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = event.target?.result as string;
        const imported = importSchemaFromJSON(json);
        if (imported) {
          saveCustomSchema(imported);
          setRefreshKey((k) => k + 1);
          const evt = new CustomEvent('table-template-imported', { detail: { schemaId: imported.id, name: imported.name } });
          window.dispatchEvent(evt);
        } else {
          toast.error(t('tableTemplates.importFailed'));
        }
      } catch (error) {
        console.error('Failed to import template:', error);
        toast.error('Failed to import template. Please check the file format.');
      }
    };
    reader.readAsText(file);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCreate = async (schemaId: string, position?: { x: number; y: number }) => {
    const diagramStage = stage;
    if (!diagramStage) return;

    const stageOffsetX = diagramStage.offsetX;
    const stageOffsetY = diagramStage.offsetY;
    const scale = diagramStage.scale;

    let x = 0;
    let y = 0;
    if (position) {
      x = (position.x - stageOffsetX) / scale;
      y = (position.y - stageOffsetY) / scale;
    } else {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      x = (viewportWidth / 2 - stageOffsetX) / scale;
      y = (viewportHeight / 2 - stageOffsetY) / scale;
    }

    const cmd = createTableFromSchema(schemaId, { x, y });
    const manager = getCommandManager();
    await manager.execute(cmd);
    if (onTemplateCreated) onTemplateCreated();
  };

  const renderThumbnail = (schema: TableSchema) => {
    const bodyRows = Math.max(1, schema.grid.rows || 1);
    const cols = Math.max(1, schema.grid.cols || 1);
    const headerRowIndices = new Set(
      (schema.rows ?? [])
        .filter((r) => r?.isHeader)
        .map((r) => r.index),
    );
    const headerColIndices = new Set(
      (schema.cols ?? [])
        .filter((c) => c?.isHeader)
        .map((c) => c.index),
    );

    const hasHeaderRow = headerRowIndices.has(0);
    const rows = hasHeaderRow ? bodyRows + 1 : bodyRows;

    const headerBg = schema.theme?.headerBackground ?? '#E5E7EB';
    const bodyBg = '#FFFFFF';
    const borderColor = schema.theme?.borderColor ?? '#CBD5E1';

    const cells: React.ReactNode[] = [];
    for (let r = 0; r < rows; r += 1) {
      for (let c = 0; c < cols; c += 1) {
        const key = `${schema.id}-${r}-${c}`;
        const isHeader = headerRowIndices.has(r) || headerColIndices.has(c);
        cells.push(
          <div
            key={key}
            className="border"
            style={{
              borderColor,
              backgroundColor: isHeader ? headerBg : bodyBg,
            }}
          />,
        );
      }
    }

    return (
      <div
        className="w-full h-full grid"
        style={{
          gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        }}
      >
        {cells}
      </div>
    );
  };

  const handleExportSchema = (schema: TableSchema) => {
    const customSchema = getCustomSchema(schema.id);
    if (customSchema) {
      const json = exportSchemaAsJSON(customSchema);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${schema.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else {
      const json = JSON.stringify(schema, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${schema.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const handleDeleteRequest = async (schema: TableSchema) => {
    const confirmed = await requestConfirmation({
      title: t('actions.delete'),
      description: t('tableTemplates.deleteConfirm', { name: schema.name }),
      confirmLabel: t('actions.delete'),
    });
    if (confirmed) handleDeleteSchema(schema.id);
  };

  const handleTemplateDragStart = (e: React.DragEvent<HTMLElement>, schema: TableSchema) => {
    if (!canWrite) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData(
      'application/x-shape-picker',
      JSON.stringify({
        category: 'tables',
        shapeId: 'table',
        schemaId: schema.id,
        label: schema.name,
      }),
    );

    const dragImage = e.currentTarget.cloneNode(true) as HTMLElement;
    dragImage.style.opacity = '0.8';
    dragImage.style.position = 'absolute';
    dragImage.style.top = '-1000px';
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 40, 40);
    setTimeout(() => {
      try {
        document.body.removeChild(dragImage);
      } catch {
        // Drag images can be detached by the browser if drag cancellation races cleanup.
      }
    }, 0);
  };

  const renderCard = (schema: TableSchema) => {
    const isCustom = isCustomSchema(schema.id);
    return (
      <div
        key={schema.id}
        className={cn(
          'flex flex-col items-stretch gap-2 rounded-lg border border-border bg-background',
          'hover:border-primary hover:bg-muted/60 transition-colors duration-150',
          'p-2 relative group'
        )}
        data-testid={`tid.tools.tableTemplates.item.${schema.id}`}
      >
        <div className="absolute top-1 right-1 z-10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-muted transition-opacity"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="w-3 h-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleExportSchema(schema);
                }}
              >
                <Download className="w-4 h-4 mr-2" />
                {t('actions.export')}
              </DropdownMenuItem>
              {isCustom && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleDeleteRequest(schema);
                  }}
                  className="text-destructive"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {t('actions.delete')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <button
          type="button"
          draggable
          onClick={() => { void handleCreate(schema.id); }}
          onDragStart={(e) => handleTemplateDragStart(e, schema)}
          className="flex flex-col items-stretch gap-2 text-left w-full"
        >
          <div className="w-full h-[80px] rounded-md bg-slate-50 border border-slate-200 overflow-hidden">
            {renderThumbnail(schema)}
          </div>
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-1.5">
              <div className="text-xs font-medium text-foreground truncate">{schema.name}</div>
              {isCustom && (
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">
                  {t('tableTemplates.custom')}
                </span>
              )}
            </div>
            <div className="text-[10px] text-muted-foreground truncate">
              {schema.category}
            </div>
          </div>
        </button>
      </div>
    );
  };

  const renderListItem = (schema: TableSchema) => {
    const isCustom = isCustomSchema(schema.id);
    return (
      <button
        key={schema.id}
        type="button"
        draggable
        onClick={() => { void handleCreate(schema.id); }}
        onDragStart={(e) => handleTemplateDragStart(e, schema)}
        className={cn(
          'group flex w-full items-center gap-3 rounded-md border border-transparent px-2.5 py-2 text-left',
          'cursor-move transition-colors duration-150',
          'hover:bg-muted/70 hover:border-muted-foreground/40 dark:hover:bg-slate-700/60 dark:hover:border-slate-500'
        )}
        data-testid={`tid.tools.tableTemplates.item.${schema.id}`}
      >
        <span className="h-10 w-14 shrink-0 overflow-hidden rounded-md border border-border bg-slate-50">
          {renderThumbnail(schema)}
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex items-center gap-1.5">
            <span className="text-xs font-medium leading-snug text-foreground break-words">
              {schema.name}
            </span>
            {isCustom && (
              <span className="shrink-0 rounded bg-primary/10 px-1.5 py-0.5 text-[9px] font-medium text-primary">
                {t('tableTemplates.custom')}
              </span>
            )}
          </span>
          <span className="truncate text-[10px] leading-tight text-muted-foreground">
            {schema.category}
          </span>
        </span>
      </button>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {confirmationDialog}
      <div className="px-3 pt-3 pb-2">
        <div className="flex items-center justify-between mb-1">
          <div className="text-xs font-medium text-foreground">{t('tableTemplates.sectionTitle')}</div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handleImport}
              className="p-1.5 rounded hover:bg-muted transition-colors"
              title={t('tableTemplates.importTemplateTooltip')}
            >
              <Upload className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        <div className="text-[11px] text-muted-foreground">
          {t('tableTemplates.sectionSubtitle')}
        </div>
        <div className="mt-2 pt-2 border-t border-border">
          <button
            type="button"
            onClick={handleImport}
            className="w-full text-xs px-2 py-1.5 rounded border border-border hover:bg-muted flex items-center justify-center gap-1.5 transition-colors"
          >
            <Upload className="w-3.5 h-3.5" />
            <span>{t('tableTemplates.importTemplate')}</span>
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-3 pb-3">
        {filteredSchemas.length > 0 ? (
          hasSearchQuery ? (
            <div
              className="flex flex-col gap-1"
              data-testid="tid.tools.tableTemplates.searchList"
            >
              {filteredSchemas.map(renderListItem)}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {filteredSchemas.map(renderCard)}
            </div>
          )
        ) : (
          <div
            className="flex h-full items-center justify-center px-4 text-center text-xs text-muted-foreground"
            data-testid="tid.tools.tableTemplates.empty"
          >
            {t('tableTemplates.noSearchResults', { defaultValue: 'No matching templates' })}
          </div>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <SaveTableTemplateDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onSave={() => {}}
        showImport={true}
      />
    </div>
  );
};
